import { fromBase64, toBase64, utf8 } from './bytes';
import { KEY_BYTES, open } from './crypto';

/**
 * The joiner's half of the pairing rendezvous, ported from
 * `shared/.../vault/cloud/PairingProtocol.kt`.
 *
 * The QR points AT the custodian: this browser mints a one-time transport
 * key, shows `https://pondering.app/pair/<sessionId>#<transportKey>`, and the
 * phone scans it. The key rides the URL FRAGMENT, so it never reaches the
 * server — the camera is the out-of-band channel a key exchange would
 * otherwise have to establish, which is why no asymmetric crypto appears
 * here.
 *
 * A browser is handed the dataKey and a grant secret, never the masterKey:
 * it can read and write, but cannot re-key the vault or mint a recovery code.
 */

export const PAIRING_HOST = 'pondering.app';
export const PAIRING_PATH_PREFIX = '/pair/';

const SESSION_ID = /^[0-9a-f]{32}$/;

export type PairingKind = 'device' | 'browser';

export interface BrowserGrantPayload {
  dataKey: Uint8Array;
  /** Presented as `vaultAuth.grantSecret` on every vault call. The record
   *  the phone lists and revokes is keyed by its SHA-256, so this value
   *  itself must never be stored or logged. */
  grantSecret: Uint8Array;
  /** Whether the custodian allowed this browser to remember its key. Absent in
   *  a payload from an older phone, which reads as no. Knowing BEFORE asking is
   *  the point: it decides whether to prompt for a guard at all. */
  persist: boolean;
}

export function generateTransportKey(): Uint8Array {
  return crypto.getRandomValues(new Uint8Array(KEY_BYTES));
}

/** Url-safe, unpadded — it lives in a URL fragment. */
export function renderPairingLink(sessionId: string, transportKey: Uint8Array): string {
  const fragment = toBase64(transportKey).replaceAll('+', '-').replaceAll('/', '_').replaceAll('=', '');
  return `https://${PAIRING_HOST}${PAIRING_PATH_PREFIX}${sessionId}#${fragment}`;
}

/** The AAD binds a payload to its session, so a blob answered for one
 *  session cannot be replayed into another. */
function sessionAad(sessionId: string): Uint8Array {
  return utf8(`pondering.pairing.v1:${sessionId}`);
}

/**
 * Null on a wrong transport key, a payload minted for another session,
 * tampering, or a shape this version does not understand — the caller cannot
 * tell which, and should not try.
 */
export function openBrowserGrant(
  transportKey: Uint8Array,
  sessionId: string,
  sealedBase64: string,
): BrowserGrantPayload | null {
  if (!SESSION_ID.test(sessionId)) return null;

  let sealed: Uint8Array;
  try {
    sealed = fromBase64(sealedBase64);
  } catch {
    return null;
  }

  const plain = open(transportKey, sessionAad(sessionId), sealed);
  if (!plain) return null;

  let payload: { v?: number; kind?: string; dataKey?: string; grantSecret?: string; persist?: boolean };
  try {
    payload = JSON.parse(new TextDecoder().decode(plain));
  } catch {
    return null;
  }
  if (payload.v !== 1 || payload.kind !== 'browser') return null;

  const dataKey = decodeKey(payload.dataKey);
  const grantSecret = decodeKey(payload.grantSecret);
  if (!dataKey || !grantSecret) return null;

  return { dataKey, grantSecret, persist: payload.persist === true };
}

function decodeKey(value: string | undefined): Uint8Array | null {
  if (typeof value !== 'string') return null;
  try {
    const bytes = fromBase64(value);
    return bytes.length === KEY_BYTES ? bytes : null;
  } catch {
    return null;
  }
}
